import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  type DropdownOption,
  LineSearchDropdown,
} from "@bloomberg/media-ui-dropdown";
import { Checkbox, createClasses, InputField } from "@bloomberg/media-ui-core";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import styles from "./styles.module.css";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import { CURRENCY_DROPDOWN_OPTIONS } from "~data/watchlist/currencies";
import { PageContext } from "~context/PageContext";
import RemainingSymbolsCount from "~components/Portfolio/RemainingSymbolsCount";
import TickerSearch, {
  type DropdownOptions,
} from "~components/Portfolio/TickerSearch";
import { DEFAULT_PORTFOLIO_FORM } from "~constants/Portfolio";
import { usePortfolioFormValidation } from "~context/PortfolioFormValidationContext";

type HeaderFields = {
  name: string;
  currency: string;
};

export default function PortfolioHeader() {
  const pageData = useContext(PageContext);
  const { portfolioForm, updatePortfolioForm } = usePortfolioPage();
  const [currencyInput, setCurrencyInput] = useState(
    portfolioForm?.currency || ""
  );
  const [formData, setFormData] = useState(
    portfolioForm || DEFAULT_PORTFOLIO_FORM
  );
  const [validationState, setValidationState] = useState<HeaderFields>({
    name: "",
    currency: "",
  });

  const { updateValidationState } = usePortfolioFormValidation();

  const validatePortfolioName = useCallback(
    (portfolioName?: string) => {
      // update validation state
      const isValidName = Boolean(portfolioName && portfolioName.length <= 30);

      updateValidationState("name", isValidName);
      updatePortfolioForm({ ...formData, name: portfolioName || "" });

      // local validation state for visual error state
      setValidationState((prevState) => {
        return {
          ...prevState,
          name: isValidName ? "" : "Invalid Watchlist Name",
        };
      });
    },
    [formData, updatePortfolioForm, updateValidationState]
  );

  const validateCurrency = useCallback(
    (currency?: string) => {
      updateValidationState("currency", Boolean(currency));
      updatePortfolioForm({ ...formData, currency: currency || "" });

      // local validation state for visual error state
      setValidationState((prevState) => {
        return {
          ...prevState,
          currency: currency ? "" : "Currency is Required",
        };
      });
    },
    [formData, updatePortfolioForm, updateValidationState]
  );

  useEffect(() => {
    if (!portfolioForm) {
      return;
    }

    setFormData(portfolioForm);
  }, [portfolioForm]);

  useEffect(() => {
    const watchlistNameInput = document.querySelector<HTMLInputElement>(
      'input[data-component="text-input"]'
    );

    if (watchlistNameInput) {
      watchlistNameInput.focus();
    }

    validatePortfolioName(portfolioForm?.name);
    validateCurrency(portfolioForm?.currency);
  }, []);

  const onNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      validatePortfolioName(event.currentTarget.value?.trim());
    },
    [validatePortfolioName]
  );

  const onCurrencyChange = useCallback(
    (value: DropdownOption | null) => {
      validateCurrency(value?.value);
    },
    [validateCurrency]
  );

  const onCurrencyTextChange = useCallback(
    (value: string) => {
      updateValidationState("currency", Boolean(value));
      setCurrencyInput(value);

      // local validation state for visual error state
      setValidationState({
        ...validationState,
        currency: value ? "" : "Currency is Required",
      });
    },
    [updateValidationState, validationState]
  );

  const currencyOptions = useMemo(() => {
    return CURRENCY_DROPDOWN_OPTIONS.filter(
      ({ value, displayName }) =>
        value.toLowerCase().includes(currencyInput.toLowerCase()) ||
        displayName?.toLowerCase?.().includes(currencyInput.toLowerCase())
    );
  }, [currencyInput]);

  const onDefaultChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updatePortfolioForm({
        ...formData,
        isDefault: event.currentTarget.checked,
      });
    },
    [formData, updatePortfolioForm]
  );

  const addHolding = useCallback(
    (ticker: string, companyName: string) => {
      const holdings = [...formData.holdings];

      const holdingIndex = holdings.findIndex(
        (holding) => holding.ticker === ticker
      );

      // If holding already exists we move it to the top of the list with new lot line
      if (holdingIndex >= 0) {
        const holding = holdings[holdingIndex];

        holding.lots.push({
          lotId: -1,
          buyDate: Date.now(),
          shares: "0",
          buyPrice: "0",
        });

        holdings[holdingIndex] = holding;
      } else {
        holdings.unshift({
          costBasis: 0,
          totalShares: 0,
          ticker,
          companyName,
          lots: [
            {
              lotId: -1,
              buyDate: Date.now(),
              shares: "0",
              buyPrice: "0",
            },
          ],
        });
      }

      updatePortfolioForm({ ...formData, holdings });
    },
    [formData, updatePortfolioForm]
  );

  const tickerOnChange = useCallback(
    (option: DropdownOptions[0] | null) => {
      if (!option) return;
      trackClickEvent("watchlistAddSymbol", "add_symbol", "click", "watchlist");
      trackClickEvent(
        "ticker search",
        `ticker-search-quote-search-module-${option.value}`,
        "click",
        "ticker search"
      );

      addHolding(option.value, option.subText || "");
    },
    [addHolding]
  );

  const currencyClassName = createClasses(styles.currencyDropdown, {
    [styles.error]: Boolean(validationState.currency),
  });

  return (
    <>
      <div className={styles.header}>
        <div className={styles.watchlistName}>
          <span className={styles.label}>Watchlist Name</span>
          <InputField
            id="watchlist-name"
            defaultValue={formData.name}
            value={formData.name}
            label=""
            size="default"
            type="text"
            onChange={onNameChange}
            error={Boolean(validationState.name)}
          />
        </div>
        <div className={styles.watchlistCurrency}>
          <span className={styles.label}>Currency</span>
          <LineSearchDropdown
            id="currency"
            className={currencyClassName}
            options={currencyOptions}
            label=""
            onChange={onCurrencyChange}
            inputValue={currencyInput}
            size="small"
            onTextChange={onCurrencyTextChange}
          />
        </div>
        <div className={styles.watchlistDefaultFlag}>
          <Checkbox
            checked={formData.isDefault}
            onChange={onDefaultChange}
            label="Set as default"
          />
        </div>
      </div>
      <div className={styles.tickerSearchWrapper}>
        <span className={styles.label}>Add Symbol</span>
        <TickerSearch
          className={styles.tickerSearch}
          countryCode={pageData?.countryCode}
          onChange={tickerOnChange}
          searchURL={pageData?.searchClientURL || ""}
        />
      </div>
      <div className={styles.remainingSymbolsWrapper}>
        <RemainingSymbolsCount holdingsCount={formData.holdings.length} />
      </div>
    </>
  );
}
